import Logger from '../../../utils/logger';

export interface WeatherModel {
  name: string;
}

export interface FavoriteCity {
  name: string;
  lat: number;
  lon: number;
}

export interface WeatherPreferences {
  customModelColors: Record<string, string>;
  favoriteWeatherVariables: string[];
  favoriteWeatherModels: Record<string, WeatherModel>;
  favoriteCities: Record<string, FavoriteCity>;
}

const STORAGE_KEYS = {
  WEATHER_VARIABLES: 'weatherPreferences_variables',
  WEATHER_MODELS: 'weatherPreferences_models',
  FAVORITE_CITIES: 'weatherPreferences_cities',
  CUSTOM_MODEL_COLORS: 'weatherPreferences_customModelColors',
  PENDING_CHANGES: 'weatherPreferences_pendingChanges',
  LAST_SUCCESSFUL_SYNC: 'weatherPreferences_lastSuccessfulSync',
} as const;

export class WeatherPreferencesStorage {
  /**
   * Get weather preferences from localStorage
   */
  static getPreferences(): WeatherPreferences {
    try {
      const localVariables = localStorage.getItem(STORAGE_KEYS.WEATHER_VARIABLES);
      const localModels = localStorage.getItem(STORAGE_KEYS.WEATHER_MODELS);
      const localCities = localStorage.getItem(STORAGE_KEYS.FAVORITE_CITIES);
      const localCustomColors = localStorage.getItem(STORAGE_KEYS.CUSTOM_MODEL_COLORS);

      return {
        customModelColors: localCustomColors ? JSON.parse(localCustomColors) : {},
        favoriteWeatherVariables: localVariables ? JSON.parse(localVariables) : [],
        favoriteWeatherModels: localModels ? JSON.parse(localModels) : {},
        favoriteCities: localCities ? JSON.parse(localCities) : {},
      };
    } catch (err) {
      Logger.error('Failed to get preferences from localStorage', err);
      return {
        customModelColors: {},
        favoriteWeatherVariables: [],
        favoriteWeatherModels: {},
        favoriteCities: {},
      };
    }
  }

  /**
   * Save weather preferences to localStorage
   */
  static savePreferences(preferences: WeatherPreferences, markAsPending = false): void {
    try {
      localStorage.setItem(
        STORAGE_KEYS.WEATHER_VARIABLES,
        JSON.stringify(preferences.favoriteWeatherVariables),
      );
      localStorage.setItem(
        STORAGE_KEYS.WEATHER_MODELS,
        JSON.stringify(preferences.favoriteWeatherModels),
      );
      localStorage.setItem(
        STORAGE_KEYS.FAVORITE_CITIES,
        JSON.stringify(preferences.favoriteCities),
      );
      localStorage.setItem(
        STORAGE_KEYS.CUSTOM_MODEL_COLORS,
        JSON.stringify(preferences.customModelColors),
      );

      if (markAsPending) {
        localStorage.setItem(STORAGE_KEYS.PENDING_CHANGES, 'true');
      }
    } catch (err) {
      Logger.error('Failed to save to localStorage', err);
    }
  }

  /**
   * Check if there are local preferences stored
   */
  static hasLocalData(): boolean {
    const localVariables = localStorage.getItem(STORAGE_KEYS.WEATHER_VARIABLES);
    const localModels = localStorage.getItem(STORAGE_KEYS.WEATHER_MODELS);
    const localCities = localStorage.getItem(STORAGE_KEYS.FAVORITE_CITIES);
    const localCustomColors = localStorage.getItem(STORAGE_KEYS.CUSTOM_MODEL_COLORS);

    return !!(localVariables || localModels || localCities || localCustomColors);
  }

  /**
   * Check if there are pending changes to sync
   */
  static hasPendingChanges(): boolean {
    return localStorage.getItem(STORAGE_KEYS.PENDING_CHANGES) === 'true';
  }

  /**
   * Mark sync as successful and clear pending changes
   */
  static markSyncSuccessful(): void {
    localStorage.removeItem(STORAGE_KEYS.PENDING_CHANGES);
    localStorage.setItem(STORAGE_KEYS.LAST_SUCCESSFUL_SYNC, Date.now().toString());
  }

  /**
   * Get last successful sync timestamp
   */
  static getLastSuccessfulSync(): number {
    const lastSync = localStorage.getItem(STORAGE_KEYS.LAST_SUCCESSFUL_SYNC);
    return lastSync ? parseInt(lastSync) : 0;
  }

  /**
   * Check if two preferences objects are different
   */
  static hasChanged(current: WeatherPreferences, updated: WeatherPreferences): boolean {
    return JSON.stringify(current) !== JSON.stringify(updated);
  }

  /**
   * Clear all weather preferences data
   */
  static clear(): void {
    Object.values(STORAGE_KEYS).forEach((key) => {
      localStorage.removeItem(key);
    });
  }
}
